import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import { isManager } from 'modules/employees/lib/isManager'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { Analytics } from 'modules/surveys/components/Analytics/SurveyAnalytics'
import SurveyShowHeader from 'modules/surveys/components/Show/SurveyShowHeader'
import SurveyShowPageTabs from 'modules/surveys/components/Show/SurveyShowPageTabs'
import type { AnalyticsFilterForm } from 'modules/surveys/hooks/useAnalyticsMultiFilters'
import { analyticsContext } from 'modules/surveys/pages/Show/hooks'
import { useSurveyShowContext } from 'modules/surveys/pages/Show/hooks'
import { AnalyticResults } from 'modules/surveys/store/collections/analyticResults'
import { QuestionGroups } from 'modules/surveys/store/collections/questionGroups'
import type { Cycle } from 'modules/surveys/store/models/AnalyticResult'
import type AnalyticResult from 'modules/surveys/store/models/AnalyticResult'
import type SurveyForm from 'modules/surveys/store/models/Form'
import type Survey from 'modules/surveys/store/models/v2/Survey'
import memberships from 'modules/teams/store/collections/memberships'
import teams from 'modules/teams/store/collections/teams'
import { userIsTeamLead } from 'modules/teams/store/userIsTeamLead'

const AnalyticsPage = () => {
  const { employee } = useCurrentSession()
  const { survey, form, hasSurveyV2, canEditSurvey, loading } = useSurveyShowContext()
  const surveyModel = hasSurveyV2 ? (survey as Survey) : (form as SurveyForm)
  const canSeeInfo =
    surveyModel?.attributes.visible_to_managers &&
    (isManager(employee) || userIsTeamLead(teams, memberships, employee.id))

  if (!survey) {
    return null
  }

  return (
    <ResponsivePage.Body
      showHeader={false}
      loading={loading}
      error={null}
      authorized={employee?.hasRegularAccess && (canEditSurvey || canSeeInfo)}
    >
      <Box gap="s24">
        <SurveyShowHeader survey={survey} canEditSurvey={canEditSurvey} />
        <SurveyShowPageTabs form={survey} canEditSurvey={canEditSurvey} />
      </Box>
      <SurveyAnalyticsProvider form={surveyModel} canEditSurvey={canEditSurvey}>
        <Analytics />
      </SurveyAnalyticsProvider>
    </ResponsivePage.Body>
  )
}

class DataParams {
  [key: string]: number | number[]
}

interface Props {
  form: SurveyForm | Survey
  canEditSurvey: boolean
}
interface Values {
  canEditSurvey: boolean
  loading: boolean
  form: SurveyForm | Survey
  analyticResults: AnalyticResults
  analyticResult: AnalyticResult | undefined | null
  questionGroups: QuestionGroups
  cycle: Cycle | null
  setCycle: (cycle: Cycle) => void
  surveyAnalyticsFilter: AnalyticsFilterForm | undefined
  setSurveyAnalyticsFilter: (surveyAnalyticsFilter: AnalyticsFilterForm) => void
}

const getSelectedFilters = (surveyAnalyticsFilter: AnalyticsFilterForm | undefined, id: number) => {
  if (!surveyAnalyticsFilter?.isActive) {
    return {
      surveys_form_id: id,
      teams: [],
      locations: [],
    }
  }

  if (surveyAnalyticsFilter?.default.length) {
    // Simple filter applied
    const simpleFilter = surveyAnalyticsFilter.default[0]
    return {
      surveys_form_id: id,
      teams: simpleFilter?.values.field === 'team' ? [simpleFilter.values.value] : [],
      locations: simpleFilter?.values.field === 'location' ? [simpleFilter.values.value] : [],
    }
  } else if (surveyAnalyticsFilter?.filters.length) {
    return {
      surveys_form_id: id,
      teams: [],
      locations: [],
    }
  }
}

const SurveyAnalyticsProvider = ({
  children,
  form,
  canEditSurvey,
}: React.PropsWithChildren<Props>) => {
  const formId = form.attributes.id
  const analyticResults = React.useRef(new AnalyticResults()).current
  const questionGroups = React.useRef(new QuestionGroups()).current
  const [cycle, setCycle] = React.useState<Cycle | null>(null)

  const [surveyAnalyticsFilter, setSurveyAnalyticsFilter] = React.useState<
    AnalyticsFilterForm | undefined
  >()

  const requestFilter = getSelectedFilters(surveyAnalyticsFilter, form.id)
  const { loading } = useAsyncData(async () => {
    const dataAnalytics: DataParams = {
      surveys_form_id: formId,
    }

    const dataQuestionGroups: DataParams = {
      surveys_form_id: formId,
    }

    if (requestFilter?.locations.length) {
      dataAnalytics.locations = requestFilter.locations
    }

    if (requestFilter?.teams.length) {
      dataAnalytics.teams = requestFilter.teams
    }

    await Promise.all([
      analyticResults.fetch({ data: dataAnalytics }),
      questionGroups.fetch({
        data: dataQuestionGroups,
      }),
    ])

    if (analyticResults.length > 1) {
      throw new Error(
        `AnalyticResults in surveys should only have one result. Survey form id: ${form.id}`
      )
    }

    if (form.hasSchedule) {
      const cyclesArray = Array.from(analyticResults.cycles)
      setCycle(cyclesArray[cyclesArray.length - 1] as Cycle)
    }
  }, [formId, requestFilter?.teams.join(','), requestFilter?.locations.join(',')])

  const values: Values = {
    canEditSurvey,
    loading,
    form,
    analyticResults,
    analyticResult: analyticResults.length ? analyticResults.at(0) : null,
    questionGroups,
    cycle,
    setCycle,
    surveyAnalyticsFilter,
    setSurveyAnalyticsFilter,
  }

  return <analyticsContext.Provider value={values}>{children}</analyticsContext.Provider>
}

export default AnalyticsPage
